/**
 * 前端访问配置的唯一入口。
 *
 * 页面里不要直接 import share.config.mjs，也不要硬编码包名/域名/商店链接，
 * 一律从这里拿——这样「配置长什么样」和「页面怎么用」是分开的两层，
 * 之后改配置不需要翻页面代码。
 */

import {
  app,
  officialDownloadUrl,
  shareLink,
  site,
  stores,
  wechat,
} from '../share.config.mjs';

export { app, officialDownloadUrl, shareLink, site, stores, wechat };

/** 站点域名根，已去掉结尾斜杠，便于安全拼接。 */
export const SITE_URL = site.url.replace(/\/+$/, '');

/**
 * 站点在域名下的子路径，规范成「以 / 开头、不带结尾斜杠」的形式。
 * 挂根路径时为空串 ''。
 */
export const BASE_PATH = (() => {
  const raw = String(site.basePath ?? '').trim().replace(/\/+$/, '');
  if (!raw || raw === '/') return '';
  return raw.startsWith('/') ? raw : `/${raw}`;
})();

/** 站点完整根地址（域名 + 子路径），分享链接、绝对地址都从这里拼。 */
export const SITE_ROOT = `${SITE_URL}${BASE_PATH}`;

/**
 * 拼 public 目录下静态资源的地址。
 *
 * 为什么必须用它而不是直接写 `/img/app-icon.png`：
 * 站点挂在 /web 下时，`/img/...` 会指到域名根，而文件实际在 /web/img 下。
 * Vite 的 base 只会改写它自己处理过的 import 和 index.html 里的引用，
 * JSX 里手写的字符串它管不到，所以统一走这个函数。
 */
export function assetUrl(path = '') {
  const clean = String(path).replace(/^\/+/, '');
  return `${BASE_PATH}/${clean}`;
}

/** 站点主机名，绑定文件与页面上展示用。 */
export const SITE_HOST = (() => {
  try {
    return new URL(SITE_URL).host;
  } catch {
    return SITE_URL;
  }
})();

/**
 * 把 `{package}` / `{appStoreId}` 之类的占位符替换成真实值。
 * 值为空时返回空串，调用方据此判断「这个入口还没配好」。
 */
function fillTemplate(template, extra = {}) {
  if (!template) return '';

  const vars = {
    package: app.android.packageName,
    appStoreId: app.ios.appStoreId,
    bundleId: app.ios.bundleId,
    ...extra,
  };

  let resolvedMissing = false;
  const filled = template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = vars[key];
    if (!value) {
      resolvedMissing = true;
      return '';
    }
    return value;
  });

  return resolvedMissing ? '' : filled;
}

/** 用本机商店 App 打开详情页的协议链接；没配或没填全则返回空串。 */
export function storeSchemeUrl(store) {
  return fillTemplate(store.scheme);
}

/** 浏览器可直接打开的商店网页版；没有稳定网页版的商店返回空串。 */
export function storeWebUrl(store) {
  return fillTemplate(store.web);
}

/**
 * 商店入口是否已经可用。
 * iOS 商店没有 App Store ID 时不算可用（会退化成搜索页，由 UI 另行处理）。
 */
export function isStoreReady(store) {
  return Boolean(storeSchemeUrl(store) || storeWebUrl(store));
}

export const iosStore = stores.find((store) => store.type === 'ios') ?? null;
export const androidStores = stores.filter((store) => store.type === 'android');

/** App Store 是否已经拿到真实 ID（决定是「直达详情页」还是「搜索应用名」）。 */
export const hasAppStoreId = Boolean(app.ios.appStoreId);

/** App Store 搜索页兜底地址：还没上架 / 还没填 ID 时用。 */
export const appStoreSearchUrl = `https://apps.apple.com/cn/search?term=${encodeURIComponent(
  app.name
)}`;

/** 安卓商店是否已经能直达详情页（取决于 App 是否已上架，属运行时事实，无法静态判断）。 */
export const hasOfficialDownload = Boolean(officialDownloadUrl);

/** 站点默认的分享卡片文案。 */
export const defaultShareTitle = `${app.name} · 同城生活`;
